import { memo } from 'react';
import type { BillStatus, PaymentMethod } from '@six-pockets/shared';
import { MoneyInput, Panel, SegmentedControl, type SegmentOption } from '@/components/ui';
import { cx } from '@/utils/cx';
import { formatINR } from '@/utils/format';
import styles from '@/styles/components/PaymentSection/style.scss';

const STATUS_OPTIONS: SegmentOption<BillStatus>[] = [
  { value: 'settled', label: 'Settled' },
  { value: 'balance', label: 'Balance' },
  { value: 'partial', label: 'Partial' },
  { value: 'waived', label: 'Waived' },
];

const METHOD_OPTIONS: SegmentOption<PaymentMethod>[] = [
  { value: 'upi', label: 'UPI' },
  { value: 'cash', label: 'Cash' },
  { value: 'split', label: 'Split' },
];

interface PaymentSectionProps {
  /** The statuses offered; all four by default (collecting a balance offers Settled and Partial). */
  statusOptions?: SegmentOption<BillStatus>[];
  status: BillStatus;
  onStatusChange: (status: BillStatus) => void;
  method: PaymentMethod;
  onMethodChange: (method: PaymentMethod) => void;
  /** Partial bills: the amount paid now (raw input text). */
  paid: string;
  onPaidChange: (value: string) => void;
  upi: string;
  onUpiChange: (value: string) => void;
  cash: string;
  onCashChange: (value: string) => void;
  balanceDue: number;
  partialHint: string | null;
  showPayment: boolean;
  /** collect − (UPI + cash) for split payments. */
  splitLeft: number;
}

/**
 * Bill status, partial amount, payment method and UPI / cash split. Memoised on plain values and stable setters,
 * so typing elsewhere in the bill (e.g. the discount, the customer search) doesn't re-render it.
 */
export const PaymentSection = memo(function PaymentSection(p: PaymentSectionProps) {
  const left = p.splitLeft;
  const target = p.status === 'partial' ? 'the amount paid' : 'the total';

  return (
    <>
      <SegmentedControl legend="Bill status" options={p.statusOptions ?? STATUS_OPTIONS} value={p.status} onChange={p.onStatusChange} />

      {p.status === 'partial' && (
        <Panel tone="amber">
          <div className={styles.partial}>
            <MoneyInput aria-label="Partial amount paid" value={p.paid} onChange={p.onPaidChange} className={styles.partialInput} />
            <div className={styles.due}>
              <span className={styles.dueLabel}>Balance due</span>
              <span className={styles.dueValue}>{formatINR(p.balanceDue)}</span>
            </div>
          </div>
          {p.partialHint && <span className={styles.warn}>{p.partialHint}</span>}
        </Panel>
      )}

      {p.showPayment && (
        <div className={styles.payment}>
          <SegmentedControl legend="Payment method" options={METHOD_OPTIONS} value={p.method} onChange={p.onMethodChange} columns={3} />

          {p.method === 'split' && (
            <>
              <div className={styles.split}>
                <MoneyInput label="UPI amount" value={p.upi} onChange={p.onUpiChange} />
                <MoneyInput label="Cash amount" value={p.cash} onChange={p.onCashChange} />
              </div>
              <span className={cx(styles.note, left === 0 ? styles.ok : styles.warn)}>
                {left === 0
                  ? `UPI + Cash matches ${target}`
                  : left > 0
                    ? `${formatINR(left)} still to split`
                    : `${formatINR(-left)} more than ${target}`}
              </span>
            </>
          )}
        </div>
      )}
    </>
  );
});
